import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { TIERS } from "@/lib/badges";
import { Lock, Printer, Medal, Download } from "lucide-react";
import Badges from "./Badges";
import Awards from "./Awards";
import Leaderboard from "./Leaderboard";
import { EvidencePanel } from "@/components/recognition/EvidencePanel";
import { PerksTab } from "@/components/recognition/PerksTab";

const TAB_IDS = ["badges", "awards", "leaderboard", "certificates", "evidence", "perks"];

/**
 * Phase 17-B item 2 (2026-07): ONE Recognition surface. The existing Badges /
 * Awards / Leaderboard pages mount unchanged as tab panels (consolidation,
 * not redesign); Certificates is a thin list linking to the existing
 * /certificate/:tierId print view. Legacy routes redirect here (App.js).
 * Signed-out visitors get Awards + Leaderboard; Badges/Certificates show a
 * sign-in prompt with a preview of the tier ladder.
 */
export default function Recognition() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  // 18-B2 item 6: Perks tab only exists inside the pilot (server decides)
  const [perks, setPerks] = useState(null);
  const loadPerks = () => api.perks().then(setPerks).catch(() => {});
  useEffect(() => { if (user) loadPerks(); }, [user]);
  // Polish: a volunteer downloads their own approved hours as CSV
  const downloadHours = async () => {
    try {
      const blob = await api.myHoursCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "my-civichearth-hours.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { /* toast-free: the button is best-effort */ }
  };
  const raw = params.get("tab");
  const tab = TAB_IDS.includes(raw) ? raw : "badges";

  return (
    <div data-testid="recognition-page">
      <Seo title="Recognition" description="Badges, awards, leaderboard and certificates — how CivicHearth celebrates volunteering." />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
        <div className="max-w-6xl mx-auto px-6 pt-10">
          <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Recognition</div>
          <h1 className="font-display text-4xl sm:text-5xl text-forest tracking-tight mt-1">Celebrate the effort</h1>
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <TabsList className="rounded-full mt-6 flex-wrap h-auto">
              <TabsTrigger data-testid="recognition-tab-badges" value="badges" className="rounded-full">Badges</TabsTrigger>
              <TabsTrigger data-testid="recognition-tab-awards" value="awards" className="rounded-full">Awards</TabsTrigger>
              <TabsTrigger data-testid="recognition-tab-leaderboard" value="leaderboard" className="rounded-full">Leaderboard</TabsTrigger>
              <TabsTrigger data-testid="recognition-tab-certificates" value="certificates" className="rounded-full">Certificates</TabsTrigger>
              <TabsTrigger data-testid="recognition-tab-evidence" value="evidence" className="rounded-full">Evidence</TabsTrigger>
              {perks?.enabled && (
                <TabsTrigger data-testid="recognition-tab-perks" value="perks" className="rounded-full">Perks</TabsTrigger>
              )}
            </TabsList>
            {user && (
              <Button data-testid="download-my-hours-btn" onClick={downloadHours} variant="outline" className="rounded-full h-9 px-4 text-xs">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download my hours (CSV)
              </Button>
            )}
          </div>
        </div>
        <TabsContent value="badges">
          {user ? <Badges /> : <SignInPreview kind="badges" />}
        </TabsContent>
        <TabsContent value="awards"><Awards /></TabsContent>
        <TabsContent value="leaderboard"><Leaderboard /></TabsContent>
        <TabsContent value="certificates">
          {user ? <CertificatesPanel /> : <SignInPreview kind="certificates" />}
        </TabsContent>
        <TabsContent value="evidence">
          {/* 17-D item 8: DofE/UCAS evidence packs + assessor reports */}
          {user ? (
            <div className="max-w-6xl mx-auto px-6 py-10"><EvidencePanel /></div>
          ) : (
            <SignInPreview kind="evidence" />
          )}
        </TabsContent>
        {perks?.enabled && (
          <TabsContent value="perks">
            <div className="max-w-6xl mx-auto px-6 py-10">
              <PerksTab perks={perks} onRefresh={loadPerks} />
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function SignInPreview({ kind }) {
  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid={`recognition-signin-${kind}`}>
      <div className="bg-white border border-neutral-200 rounded-2xl p-10 text-center">
        <Lock className="w-8 h-8 text-forest/30 mx-auto" />
        <div className="font-display text-2xl text-forest mt-3">
          Sign in to see your {kind}
        </div>
        <p className="text-sm text-neutral-600 mt-2 max-w-md mx-auto">
          Every approved volunteering hour counts towards tiered badges and printable certificates.
        </p>
        <div className="flex items-center justify-center gap-2 flex-wrap mt-6">
          {TIERS.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1.5 text-xs font-medium bg-cream border border-forest/10 rounded-full px-3 py-1.5 text-forest">
              <Medal className="w-3.5 h-3.5" style={{ color: t.color }} /> {t.label} · {t.threshold}h
            </span>
          ))}
        </div>
        <Link to="/login">
          <Button data-testid={`recognition-signin-cta-${kind}`} className="mt-6 rounded-full bg-forest hover:bg-forest-hover px-7">Sign in</Button>
        </Link>
      </div>
    </div>
  );
}

function CertificatesPanel() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.myStats().then(setStats).catch(() => setStats({ total_hours: 0 })); }, []);
  const hours = stats?.total_hours || 0;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="recognition-certificates-panel">
      <p className="text-sm text-neutral-600 max-w-xl">
        Certificates unlock with each badge tier — print them or attach them to CV,
        DofE and university applications. You have <b>{hours}h</b> approved.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        {TIERS.map((t) => {
          const earned = hours >= t.threshold;
          return (
            <div key={t.id} className={`bg-white border rounded-2xl p-6 flex items-center gap-4 ${earned ? "border-forest/25" : "border-neutral-200 opacity-70"}`}>
              <Medal className="w-8 h-8 shrink-0" style={{ color: t.color }} />
              <div className="flex-1">
                <div className="font-display text-lg text-forest">{t.label} certificate</div>
                <div className="text-xs text-neutral-500">{earned ? "Earned — ready to print" : `Unlocks at ${t.threshold} approved hours`}</div>
              </div>
              {earned ? (
                <Link to={`/certificate/${t.id}`} data-testid={`certificate-link-${t.id}`}>
                  <Button variant="outline" className="rounded-full h-9 px-4 text-sm border-forest/25 text-forest hover:bg-forest/5">
                    <Printer className="w-4 h-4 mr-1.5" /> View
                  </Button>
                </Link>
              ) : (
                <Lock className="w-4 h-4 text-neutral-300" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
